import React, { useRef, useEffect, useCallback } from 'react';
import Message from './Message';
import ChatInput from './ChatInput';

function ChatArea({
  messages,
  contentLibrary,
  inputText,
  onInputChange,
  onInputSubmit,
  onOptionClick
}) {
  const latestMessageRef = useRef(null);
  const previousMessageCountRef = useRef(messages.length);
  const previousLatestMessageIdRef = useRef(null);
  const scrollFrameRef = useRef(null);

  const scheduleRevealLatestAnswer = useCallback(() => {
    if (scrollFrameRef.current !== null) {
      cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      latestMessageRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start'
      });
    });
  }, []);

  useEffect(() => {
    const appended = messages.length > previousMessageCountRef.current;
    const latestMessageId = messages[messages.length - 1]?.id ?? null;
    const replaced = latestMessageId !== null
      && previousLatestMessageIdRef.current !== null
      && latestMessageId !== previousLatestMessageIdRef.current;
    previousMessageCountRef.current = messages.length;
    previousLatestMessageIdRef.current = latestMessageId;
    if ((appended && messages.length > 1) || replaced) {
      scheduleRevealLatestAnswer();
    }
    return () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [messages, scheduleRevealLatestAnswer]);

  return (
    <section className="conversation" aria-labelledby="conversation-title">
      <div className="conversation__heading">
        <p className="eyebrow">Ask me anything</p>
        <h2 id="conversation-title">Explore my projects and experience</h2>
      </div>
      <ChatInput
        inputText={inputText}
        onInputChange={onInputChange}
        onSubmit={onInputSubmit}
      />
      <div className="messages" data-testid="messages" aria-live="polite">
          {messages.map((message, index) => (
            <Message
              key={message.id}
              message={message}
              onOptionClick={onOptionClick}
              messageRef={index === messages.length - 1 ? latestMessageRef : null}
              presentation={
                contentLibrary.find(item => item.id === message.contentId)?.presentation
              }
            />
          ))}
      </div>
    </section>
  );
}

export default ChatArea;
